import ContentPageHeroSlide from 'components/content-page-hero-slide/content-page-hero-slide';
import PaginationNav from 'components/control/pagination-nav';
import BlogCard from 'components/blog-card';
import styles from './style.module.scss';
import { useRef } from 'react';
import { BlogArticle } from 'data/blog-article';
import { PropsWithTestId } from 'types';
import { contentPageIds } from 'constants/test-constants';
import useSizeClass from 'hooks/use-size-class';
import { buildClassName } from 'utils/build-class-name';
import HeadingWithImageFill from 'components/blog-page/heading-with-image-fill';

export const DEFAULT_HERO_SLIDE_BACKGROUND_IMAGE_SRC = '/assets/images/inservice/inservice-life-hero.png';

export type BlogPost = BlogArticle & {
  label?: string;
};

export enum BLOG_PAGE_HERO_TYPE {
  HERO_SLIDE = 'HERO_SLIDE',
  IMAGE_IN_TEXT = 'IMAGE_IN_TEXT'
}

interface BlogPageHeroSlideProps {
  backgroundImageSrc: string;
  topText: string;
  bottomText: string;
}

interface BlogPageImageInTextProps {
  backgroundImageSrc: string;
  title: string;
  description: string;
}

export interface BlogPageHeroProps {
  type: BLOG_PAGE_HERO_TYPE;
  props: BlogPageHeroSlideProps | BlogPageImageInTextProps;
}

/**
 * The props for the BlogPage component.
 *
 * @param testId The test ID for the component. Default is {@link contentPageIds.blogPost}.
 */
export type BlogPageProps = PropsWithTestId<{
  /** The blog articles to display. */
  posts: BlogPost[];
  /** The current page number. */
  pageNumber: number;
  /** The total number of pages. */
  pagesCount: number;
  /** The hero slide to display. */
  heroProps: BlogPageHeroProps;
  /** The function to navigate to a specific page. */
  goToPage: (page: number) => void;
}>;

const BlogPage = ({
  posts,
  pageNumber,
  pagesCount,
  heroProps,
  testId = contentPageIds.blogPost,
  goToPage } : BlogPageProps) => {
  const postsGridRef = useRef<HTMLDivElement>(null);
  const isMobile = useSizeClass({ max: 1000 });

  const heroSlide = () => {
    switch (heroProps.type) {
      case BLOG_PAGE_HERO_TYPE.HERO_SLIDE:
        return <ContentPageHeroSlide
          backgroundImageSrc={heroProps.props.backgroundImageSrc}
          downArrowRef={postsGridRef}
          topText={(heroProps.props as BlogPageHeroSlideProps).topText}
          bottomText={(heroProps.props as BlogPageHeroSlideProps).bottomText}
          testId={`${testId}-hero-slide`}
        />;
      case BLOG_PAGE_HERO_TYPE.IMAGE_IN_TEXT:
        return <HeadingWithImageFill
          backgroundImageSrc={heroProps.props.backgroundImageSrc}
          title={(heroProps.props as BlogPageImageInTextProps).title}
          description={(heroProps.props as BlogPageImageInTextProps).description}
          testId={`${testId}-hero-image-in-text`}
        />; 
      default:
        return null;
    }
  };

  return (
    <div className={styles.component} data-testid={`${testId}-container`}>
      {heroSlide()}
      <div className={buildClassName(styles['posts-grid'], pageNumber === 1 && styles['first-page-grid'])} ref={postsGridRef} data-testid={`${testId}-posts-grid`}>
        {posts.map(({ id, title, link, thumbnail, date, excerpt, label }, index) => {
          const isFeaturedCard = index === 0 && pageNumber === 1 && !isMobile;
          return (
            <BlogCard
              key={id}
              title={title}
              link={link}
              topLabel={label}
              imageSrc={thumbnail}
              date={date}
              excerpt={excerpt}
              isFeaturedCard={isFeaturedCard}
              hideExcerpt={true}
            />
          );
        })}
      </div>
      <div className={styles.pagination} data-testid={`${testId}-pagination`}>
        <PaginationNav
          pageNumber={pageNumber}
          totalPages={pagesCount}
          setPageNumber={goToPage}
        />
      </div>
    </div>
  );
};

export default BlogPage;
